// Vendor console, level 2 — one vendor: how its data reaches us, and its
// products with what each is still missing.
//
// 🔴 SERVICES ARE LISTED INDIVIDUALLY, never rolled into one "connected" badge.
// Gemline reads product data ✓ / media ✓ / pricing ✗, and that third line is
// the whole diagnosis of why 1,320 products cannot be quoted. A single
// connected/disconnected pill would hide it exactly as every other screen did.
//
// Read-only for now. The ingest actions (whole vendor / selected / one row) are
// designed in docs/vendor-ingestion-app.md §9b and land with write access.

import type React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";
import { SupplierAccountSection } from "@/components/SupplierAccountSection";
import { cn } from "@/lib/utils";
import type { VendorConsoleRow } from "@merchd/shared";

interface ServiceRow {
  service: string;
  environment: string;
  isActive: boolean;
  lastIngestedAt: string | null;
}
interface ProductRow {
  id: string;
  sku: string | null;
  name: string;
  source: string | null;
  breaks: number;
  hasSupplierRetail: boolean;
  hasImage: boolean;
  hasDescription: boolean;
}
interface DetailResponse {
  vendor: VendorConsoleRow;
  services: ServiceRow[];
  products: ProductRow[];
  productTotal: number;
}

const SERVICE_LABEL: Record<string, string> = {
  product_data: "Product data",
  media_content: "Media",
  pricing_configuration: "Pricing",
  inventory: "Inventory",
  purchase_order: "Purchase order",
  order_status: "Order status",
  invoice: "Invoice",
};

// The services that decide whether a vendor is sellable. Listed even when NOT
// declared, because an absent row is the finding — a screen that only renders
// what exists cannot show you what is missing.
const CORE_SERVICES = ["product_data", "media_content", "pricing_configuration"];

export default function VendorDetailPage(): React.ReactElement {
  const { vendorId } = useParams<{ vendorId: string }>();
  const { user } = useAuth();
  // Admins and production reps: the people who keep supplier accounts and
  // order inboxes current (the API permits both — routes/vendors.ts).
  const roles = user?.roles ?? [];
  const isStaff = roles.includes("admin") || roles.includes("production_rep");
  const canEdit = isStaff;

  const { data, isLoading, error } = useQuery<DetailResponse>({
    queryKey: ["vendor-console", vendorId],
    queryFn: () => api(`/vendor-console/${vendorId}`),
    enabled: isStaff && Boolean(vendorId),
  });

  if (!isStaff) {
    return <div className="p-6 text-sm text-brown-400">Admins and production reps only.</div>;
  }
  if (isLoading) {
    return <div className="p-6 text-sm text-brown-300">Loading…</div>;
  }
  if (error || !data) {
    return (
      <div className="p-6 text-sm text-rust-600">Could not load this vendor.</div>
    );
  }

  const { vendor, services, products, productTotal } = data;
  const declared = new Map(
    services
      .filter((s) => s.environment === "production")
      .map((s) => [s.service, s]),
  );
  const extra = services.filter(
    (s) => s.environment === "production" && !CORE_SERVICES.includes(s.service),
  );

  return (
    <div className="space-y-5 p-6">
      <nav className="text-xs text-brown-300">
        <Link to="/admin/vendors" className="hover:text-rust-500 hover:underline">
          Vendors
        </Link>
        <span className="mx-1.5">›</span>
        <span className="text-brown-500">{vendor.vendorName}</span>
      </nav>

      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-xl font-semibold text-brown-600">
          {vendor.vendorName}
        </h1>
        <StatePill state={vendor.state} />
      </header>
      {vendor.nextAction && (
        <p className="text-sm text-rust-600">{vendor.nextAction}</p>
      )}
      {vendorId && <SupplierAccountSection vendorId={vendorId} canEdit={canEdit} />}

      <section className="rounded-md border border-brown-100 bg-white p-4">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Source
        </p>
        <dl className="space-y-1 text-sm">
          <Row label="Kind" value={kindLabel(vendor.kind)} />
          {CORE_SERVICES.map((s) => {
            const row = declared.get(s);
            return (
              <Row
                key={s}
                label={SERVICE_LABEL[s] ?? s}
                value={
                  row === undefined
                    ? "Not declared"
                    : row.lastIngestedAt
                      ? `Declared, ran ${new Date(row.lastIngestedAt).toLocaleDateString()}`
                      : "Declared, never run"
                }
                tone={
                  row === undefined
                    ? "bad"
                    : row.lastIngestedAt
                      ? "good"
                      : "warn"
                }
              />
            );
          })}
          {extra.map((s) => (
            <Row
              key={s.service}
              label={SERVICE_LABEL[s.service] ?? s.service}
              value={s.isActive ? "Declared" : "Declared, inactive"}
            />
          ))}
        </dl>
      </section>

      <section>
        <p className="mb-2 text-xs text-brown-400">
          Showing {products.length.toLocaleString()} of{" "}
          {productTotal.toLocaleString()} products — least complete first.
        </p>
        <div className="overflow-x-auto rounded-md border border-brown-100">
          <table className="w-full text-sm">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="px-3 py-2">SKU</th>
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">Price</th>
                <th className="px-3 py-2">Retail</th>
                {/* "Ours" — which assets are merch'd's rather than the
                    supplier's. It is how "make it our own" becomes visible
                    progress instead of an ambition. */}
                <th className="px-3 py-2">Ours</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-brown-50/70">
                  <td className="px-3 py-2 font-mono text-xs text-brown-500">
                    {p.sku ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-brown-500">{p.name}</td>
                  <td className="px-3 py-2">
                    {p.breaks === 0 ? (
                      <span className="text-rust-600">None</span>
                    ) : p.breaks === 1 ? (
                      <span className="text-brown-400">Flat</span>
                    ) : (
                      <span className="text-brown-500">
                        {p.breaks} breaks
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {p.hasSupplierRetail ? (
                      <span className="text-brown-400">Supplier</span>
                    ) : (
                      <span className="text-rust-500">Ours</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-brown-400">
                    {[
                      p.hasImage ? "Img" : null,
                      p.hasDescription ? "Desc" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-brown-300">
                    No products loaded for this vendor yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-brown-300">
        &ldquo;Retail: Ours&rdquo; means the supplier published no usable retail
        and the sell price was calculated by us, not sent by them.
      </p>
    </div>
  );
}

function Row(props: {
  label: string;
  value: string;
  tone?: "good" | "warn" | "bad";
}): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-brown-400">{props.label}</dt>
      <dd
        className={cn(
          "text-right",
          props.tone === "bad" && "font-semibold text-rust-600",
          props.tone === "warn" && "text-rust-500",
          props.tone === "good" && "text-brown-500",
          !props.tone && "text-brown-500",
        )}
      >
        {props.value}
      </dd>
    </div>
  );
}

function StatePill(props: {
  state: VendorConsoleRow["state"];
}): React.ReactElement {
  const label: Record<VendorConsoleRow["state"], string> = {
    quotable: "Quotable",
    flat_priced: "Flat priced",
    catalogue_only: "Catalogue only",
    configured_not_run: "Not yet run",
    not_started: "Not started",
  };
  const tone: Record<VendorConsoleRow["state"], string> = {
    quotable: "bg-peach-100 text-brown-500",
    flat_priced: "bg-cream-100 text-brown-500",
    catalogue_only: "border border-rust-300 bg-rust-50 text-rust-600",
    configured_not_run: "border border-brown-200 text-brown-400",
    not_started: "border border-brown-100 text-brown-300",
  };
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        tone[props.state],
      )}
    >
      {label[props.state]}
    </span>
  );
}

function kindLabel(kind: VendorConsoleRow["kind"]): string {
  switch (kind) {
    case "promostandards":
      return "PromoStandards";
    case "bulk_file":
      return "Bulk file";
    case "esp":
      return "ESP";
    case "manual":
      return "Manual";
    default:
      return "—";
  }
}
